import {
  ColDef,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  ProcessCellForExportParams,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import {
  Directive,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosDefaultColDef,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import { FilterModalService } from '@helios/filter-modal'
import { countFilters } from '@helios/server-side-util'
import {
  RootState,
  setFocusedApprovalsAccountId,
} from '@helios/state-management'
import {
  createFilterGroupsToolPanel,
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { Store } from '@ngrx/store'
import { AgGridApi } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import { formatDate, ISODateTimeFormat } from '@northfork/basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { of, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { ApprovalsKey } from '../../../panel-key'

@Directive()
export abstract class RequestDetailGridBase<T> implements OnInit, OnDestroy {
  gridApi?: AgGridApi
  data: T[] = []
  isLoading = false
  clearFiltersEnabled = false
  filterCount = 0
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  savedFilterState: { [columnName: string]: unknown } | undefined
  protected readonly destroySubject$ = new Subject<void>()

  @Output()
  refreshRequest = new EventEmitter<boolean>()

  @Output()
  requestDetailsSelectedRow = new EventEmitter<T[]>()

  protected abstract gridKey: string

  constructor(
    protected readonly preferences: UserPreferencesService,
    protected readonly savedViewService: WorkspaceGridSavedViewService,
    protected readonly store: Store<RootState>,
    public filterModalService: FilterModalService,
  ) {}

  ngOnInit(): void {
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  abstract createColumnDefs(): ColDef[]

  createGridOptions(): GridOptions {
    return {
      ...heliosMultiRowSelectionConfig,
      getContextMenuItems: params => this.getContextMenuItems(params),
      defaultColDef: {
        ...heliosDefaultColDef,
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      columnDefs: this.createColumnDefs(),
      onSelectionChanged: event => {
        this.onRowSelectionChanged(event)
      },
      onFirstDataRendered: () => this.applyFiltersAfterFirstRender(),
      onFilterChanged: params => this.onFilterChanged(params),
      onGridReady: params => this.onGridReady(params),
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (params.column.getColDef().type === 'DateTime' && params.value) {
          return formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat)
        }
        return params.value
      },
    }
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    this.filterModalService.registerGrid(
      this.gridApi?.api,
      ApprovalsKey,
      this.gridKey,
    )

    enableHeliosMultiRowDragSelection(this.gridApi.api)
    enableHeliosCopyBehavior(this.gridApi.api)

    const agGridStateSaved = await this.savedViewService.manageSavedView(
      params,
      this.gridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )
    this.savedFilterState = agGridStateSaved.filterState
    this.applyGridConfig(agGridStateSaved)
  }

  applyFiltersAfterFirstRender() {
    if (Object.keys(this.savedFilterState ?? {}).length) {
      if (this.savedFilterState) {
        this.gridApi?.api.setFilterModel(this.savedFilterState)
      }
    }
  }

  getContextMenuItems(params: GetContextMenuItemsParams) {
    const menuItems: ('separator' | MenuItemDef)[] = []

    let rowsSelected: T[]
    if (!params?.node?.id) {
      return menuItems
    }

    if (params.api.getSelectedNodes().length > 1) {
      rowsSelected = params.api.getSelectedNodes().map(t => t.data) ?? []
    } else {
      rowsSelected = [params.node.data] as T[]
    }

    menuItems.push({
      name: `Filter to Accounts`,
      action: () => this.filterAccounts(rowsSelected),
      disabled: !rowsSelected.every(x => this.hasAccountId(x)),
    })

    return menuItems
  }

  protected abstract hasAccountId(row: T): boolean

  filterAccounts(selectedRows: T[]) {
    const accountIds = selectedRows.map(x => this.getAccountId(x))
    this.store.dispatch(setFocusedApprovalsAccountId(accountIds))
  }

  protected abstract getAccountId(row: T): number

  onRowSelectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedNodes().map(x => x.data as T)

    this.requestDetailsSelectedRow.emit(selectedRows)
  }

  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: this.gridKey,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
  }

  onFilterChanged(params: FilterChangedEvent) {
    const filtersPresent = this.gridApi?.api.isAnyFilterPresent() ?? false
    this.clearFiltersEnabled = filtersPresent
    this.countFilters()
  }

  private countFilters() {
    if (!this.gridApi) {
      return
    }
    const filterModels = this.gridApi?.api.getFilterModel() ?? {}
    const activeFilters = Object.keys(filterModels)
    this.clearFiltersEnabled = activeFilters.length > 0
    this.filterCount = countFilters(this.gridApi?.api) ?? 0
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }
}
